/**
 * React Query bindings for the B1.7 CashierView.
 *
 * Cache keys:
 *   - ['cashier', 'snapshot', branchId]
 *
 * Stale windows:
 *   - staleTime: 15s — cashier is a "live" surface; cart/totals must be fresh
 *   - refetchOnWindowFocus: true (inherited from App-level default)
 *
 * Design note: every mutation returns the new CashierSnapshot, so mutation
 * onSuccess handlers simply setQueryData the returned snapshot. No optimistic
 * update for lifecycle operations (park/void/commit) — those need server
 * authority; optimistic only for cart line edits which are cheap to rollback.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  addCartItem,
  applyDiscount,
  closeSession,
  commitTender,
  getCashierSnapshot,
  parkOrder,
  removeCartItem,
  resumeParked,
  updateCartQty,
  voidOrder,
} from './cashier-api';
import type {
  AddCartItemRequest,
  ApplyDiscountRequest,
  CashierSnapshot,
  CloseSessionRequest,
  CommitTenderRequest,
  CommitTenderResult,
  ParkOrderRequest,
  RemoveCartItemRequest,
  ResumeParkedRequest,
  UpdateCartQtyRequest,
  VoidOrderRequest,
} from './types';

export const cashierKeys = {
  all: ['cashier'] as const,
  snapshot: (branchId: string) =>
    [...cashierKeys.all, 'snapshot', branchId] as const,
};

export function useCashierSnapshot(branchId: string | undefined) {
  return useQuery({
    queryKey: branchId
      ? cashierKeys.snapshot(branchId)
      : [...cashierKeys.all, 'snapshot', '__pending__'],
    queryFn: () => getCashierSnapshot({ branchId: branchId! }),
    enabled: Boolean(branchId),
    staleTime: 15_000,
  });
}

function useSnapshotMutation<TRequest>(
  branchId: string | undefined,
  mutationFn: (req: TRequest) => Promise<CashierSnapshot>,
) {
  const qc = useQueryClient();
  return useMutation<CashierSnapshot, Error, TRequest>({
    mutationFn,
    onSuccess: (snapshot) => {
      if (!branchId) return;
      qc.setQueryData<CashierSnapshot>(cashierKeys.snapshot(branchId), snapshot);
    },
  });
}

export function useAddCartItem(branchId: string | undefined) {
  return useSnapshotMutation<AddCartItemRequest>(branchId, addCartItem);
}

export function useUpdateCartQty(branchId: string | undefined) {
  return useSnapshotMutation<UpdateCartQtyRequest>(branchId, updateCartQty);
}

export function useRemoveCartItem(branchId: string | undefined) {
  return useSnapshotMutation<RemoveCartItemRequest>(branchId, removeCartItem);
}

export function useApplyDiscount(branchId: string | undefined) {
  return useSnapshotMutation<ApplyDiscountRequest>(branchId, applyDiscount);
}

export function useParkOrder(branchId: string | undefined) {
  return useSnapshotMutation<ParkOrderRequest>(branchId, parkOrder);
}

export function useResumeParked(branchId: string | undefined) {
  return useSnapshotMutation<ResumeParkedRequest>(branchId, resumeParked);
}

export function useVoidOrder(branchId: string | undefined) {
  return useSnapshotMutation<VoidOrderRequest>(branchId, voidOrder);
}

export function useCloseSession(branchId: string | undefined) {
  return useSnapshotMutation<CloseSessionRequest>(branchId, closeSession);
}

/**
 * Commit tender is the only mutation that does NOT return a snapshot directly;
 * it returns a CommitTenderResult with a receipt id (used by B1.9 preview).
 * After commit, we invalidate to refetch the fresh snapshot (totals, active
 * order cleared, parked list unchanged, session GMV updated).
 */
export function useCommitTender(
  branchId: string | undefined,
  options?: UseMutationOptions<CommitTenderResult, Error, CommitTenderRequest>,
) {
  const qc = useQueryClient();
  return useMutation<CommitTenderResult, Error, CommitTenderRequest>({
    ...options,
    mutationFn: commitTender,
    onSuccess: async (...args) => {
      if (branchId) {
        await qc.invalidateQueries({
          queryKey: cashierKeys.snapshot(branchId),
        });
      }
      await options?.onSuccess?.(...args);
    },
  });
}
